/**
 * WhatsApp Sections Service
 * Manages WhatsApp recipient configuration per notification type.
 * Stored in Firestore at: Utils/Whatsapp/Sections/{sectionId}
 */
import { getFirebaseDb } from '@core/firebase/firebase.js'
import { doc, getDoc, getDocs, setDoc, collection } from 'firebase/firestore'

/** Default section IDs with Hebrew descriptions */
export const WHATSAPP_SECTIONS = {
  inventoryAlert: 'התראת מלאי בוואטסאפ',
  // Backup recipients for the obligo-breach alert: used only when the client's
  // assigned agent cannot be resolved to a user with a phone number.
  obligoAlert: 'גיבוי להתראת חריגת אובליגו',
  // Recipients for the manual-amount collection alert (Gvia screen, fired on input blur)
  manualGviaAlert: 'התראת גביית סכום ידנית',
  // Test recipients for the daily order-day / weekly-benefit promo WhatsApp (cron preview)
  orderDayPromosPreview: 'בדיקת הודעת יום ההזמנה / הטבה שבועית',
}

/** Section id whose recipients receive the order-day / weekly-benefit preview */
export const ORDER_DAY_PROMOS_PREVIEW_SECTION = 'orderDayPromosPreview'

const SECTIONS_PATH = 'Utils/WhatsAppMsgAdmin/Sections'

/**
 * Get a single WhatsApp section document
 * @param {string} sectionId
 * @returns {Promise<{id: string, description: string, recipients: Array<{name: string, phone: string}>} | null>}
 */
export async function getWhatsappSection(sectionId) {
  const db = getFirebaseDb()
  const snap = await getDoc(doc(db, SECTIONS_PATH, sectionId))
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() }
}

/**
 * Get recipients array for a section (returns [] if not found)
 * @param {string} sectionId
 * @returns {Promise<Array<{name: string, phone: string}>>}
 */
export async function getWhatsappRecipients(sectionId) {
  const section = await getWhatsappSection(sectionId)
  if (!section || !Array.isArray(section.recipients)) return []
  return section.recipients
}

/**
 * Get all WhatsApp section documents
 * @returns {Promise<Array<{id: string, description: string, recipients: Array<{name: string, phone: string}>}>>}
 */
export async function getAllWhatsappSections() {
  const db = getFirebaseDb()
  const snap = await getDocs(collection(db, SECTIONS_PATH))
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
}

/**
 * Save/update a WhatsApp section document
 * @param {string} sectionId
 * @param {{ description: string, recipients: Array<{name: string, phone: string}> }} data
 */
export async function saveWhatsappSection(sectionId, { description, recipients }) {
  const db = getFirebaseDb()
  await setDoc(doc(db, SECTIONS_PATH, sectionId), { description, recipients }, { merge: true })
}

/**
 * Seed default WhatsApp sections if they don't exist.
 */
export async function seedWhatsappSections() {
  const db = getFirebaseDb()
  for (const [sectionId, description] of Object.entries(WHATSAPP_SECTIONS)) {
    const ref = doc(db, SECTIONS_PATH, sectionId)
    const snap = await getDoc(ref)
    if (!snap.exists()) {
      await setDoc(ref, {
        description,
        recipients: [],
      })
    }
  }
}
